import React, { useState, useEffect, useMemo } from "react";
import "../../../css/CentersTable.css";
import api from "../../../api/axiosConfig";
import ReleaseAppointmentsModal from "./ReleaseAppointmentsModal";
import CampReleaseAppointmentsModal from "./CampReleaseAppointmentsModal";
import { showToast } from '../../Shared/Toaster';

// Interface for the center data
interface Center {
  _id: string;
  center_id: string;
  name: string;
  centerType: string;
  city: string;
  isActive: boolean;
  createdAt: string;
  disabledAt?: string | null;
}

interface Props {
  onReleaseSuccess?: () => void;
}

const CentersTable: React.FC<Props> = ({ onReleaseSuccess }) => {
  const [centers, setCenters] = useState<Center[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedQavachCenter, setSelectedQavachCenter] = useState<Center | null>(null);
  const [selectedCampCenter, setSelectedCampCenter] = useState<Center | null>(null);

  // State for search, category, city filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchCenters = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = sessionStorage.getItem("accessToken");
        let allCenters: Center[] = [];
        let page = 1;
        const apiPageLimit = 10;

        while (true) {
          const res = await api.center_api.get(
            `/api/v1/centers?page=${page}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          const pageData = res.data?.data || [];

          if (pageData.length > 0) {
            allCenters = [...allCenters, ...pageData];
          }

          if (pageData.length < apiPageLimit) {
            break;
          }

          page++;
        }

        setCenters(allCenters);
      } catch (err) {
        setError("Failed to load center data.");
      } finally {
        setLoading(false);
      }
    };
    fetchCenters();
  }, []);

  // Derive unique categories and cities for dropdown options
  const uniqueCategories = useMemo(() => {
    const categories = centers
      .map((c) => c.centerType)
      .filter((type): type is string => Boolean(type));
    return Array.from(new Set(categories));
  }, [centers]);

  const uniqueCities = useMemo(() => {
    const cities = centers
      .map((c) => c.city)
      .filter((city): city is string => Boolean(city));
    return Array.from(new Set(cities));
  }, [centers]);

  // Memoized filtering logic by search term, category, and city
  const filteredCenters = useMemo(() => {
    return centers.filter((center) => {
      const matchesSearch =
        !searchTerm ||
        center.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        center.city.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        !selectedCategory || center.centerType === selectedCategory;

      const matchesCity =
        !selectedCity || center.city === selectedCity;

      return matchesSearch && matchesCategory && matchesCity;
    });
  }, [centers, searchTerm, selectedCategory, selectedCity]);

  // Reset page to 1 when search or filter terms change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedCity]);

  // Pagination logic
  const totalPages = Math.ceil(filteredCenters.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCentersToDisplay = filteredCenters.slice(startIndex, endIndex);

  const handleReleaseClick = (center: Center) => {
    if (center.centerType === "QAVACH CENTER") {
      setSelectedQavachCenter(center);
    } else if (center.centerType === "CAMP CENTER") {
      setSelectedCampCenter(center);
    } else {
      showToast(`Functionality not available for type: ${center.centerType}`, 'error');
    }
  };

  if (loading) return <div className="centers-table"><p>Loading centers...</p></div>;
  if (error) return <div className="centers-table"><p style={{ color: "red" }}>{error}</p></div>;

  return (
    <>
      <div className="centers-table">
        <div className="release-apt-table-header">
          <h3>List of Active Centers</h3>
          
          {/* Filters Bar: Center Category, City, Search input */}
          <div className="filter-controls-bar">
            <select
              className="center-filter-dropdown"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">Center Category</option>
              {uniqueCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            <select
              className="center-filter-dropdown"
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
            >
              <option value="">City</option>
              {uniqueCities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Search by Center Name"
              className="search-box"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>Center Name</th>
              <th>Center Category</th>
              <th>City</th>
              <th>Status</th>
              <th>Created On</th>
              <th>Disabled On</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentCentersToDisplay.length > 0 ? (
              currentCentersToDisplay.map((center, index) => (
                <tr key={center._id}>
                  <td>{startIndex + index + 1}</td>
                  <td>{center.name}</td>
                  <td><span className="tag">{center.centerType}</span></td>
                  <td>{center.city}</td>
                  <td>
                    <span className={`roles-page-status ${center.isActive ? "roles-page-status-active" : "roles-page-status-inactive"}`}>
                      {center.isActive ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td>{new Date(center.createdAt).toLocaleDateString()}</td>
                  <td>{center.disabledAt ? new Date(center.disabledAt).toLocaleDateString() : "-"}</td>
                  <td>
                    <button className="release-btn" onClick={() => handleReleaseClick(center)}>
                      Create Appointments
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={8} style={{ textAlign: "center" }}>No centers match your search/filters.</td></tr>
            )}
          </tbody>
        </table>
        
        {/* Dynamic Pagination Footer */}
        {totalPages > 0 && (
          <div className="pagination-footer">
            <p>Showing {startIndex + 1} to {Math.min(endIndex, filteredCenters.length)} of {filteredCenters.length} results</p>
            <div className="pagination">
              <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>Previous</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                <button key={pageNumber} onClick={() => setCurrentPage(pageNumber)} className={currentPage === pageNumber ? "active" : ""}>{pageNumber}</button>
              ))}
              <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedQavachCenter && (
        <ReleaseAppointmentsModal
          isOpen={!!selectedQavachCenter}
          onClose={() => setSelectedQavachCenter(null)}
          onSuccess={onReleaseSuccess}
          center={selectedQavachCenter}
        />
      )}
      {selectedCampCenter && (
        <CampReleaseAppointmentsModal
          isOpen={!!selectedCampCenter}
          onClose={() => setSelectedCampCenter(null)}
          onSuccess={onReleaseSuccess}
          center={selectedCampCenter}
        />
      )}
    </>
  );
};

export default CentersTable;